import { create } from 'zustand';
import { supabase } from '@/lib/supabase';

export interface GroupMember {
  playerId: string;
  runnerName: string;
  completedAt: number | null;
}

export interface GroupSession {
  id: string;
  code: string;
  leaderId: string;
  vaultId: string;
  status: 'waiting' | 'active' | 'expired';
  members: GroupMember[];
  expiresAt: number;
}

interface GroupState {
  currentSession: GroupSession | null;
  loading: boolean;
  error: string | null;

  createSession: (vaultId: string, leaderId: string, runnerName: string) => Promise<{ ok: boolean; code?: string }>;
  joinSession: (code: string, playerId: string, runnerName: string) => Promise<{ ok: boolean; vaultId?: string }>;
  leaveSession: (playerId: string) => Promise<void>;
  markCompleted: (playerId: string) => Promise<void>;
  fetchSession: (sessionId: string) => Promise<void>;
  clearSession: () => void;
  subscribeRealtime: (sessionId: string) => () => void;
}

function randomCode(): string {
  return Math.random().toString(36).slice(2, 8).toUpperCase();
}

export function groupLootBonus(session: GroupSession | null): number {
  if (!session) return 1.0;
  const completed = session.members.filter((m) => m.completedAt !== null).length;
  if (completed < 2) return 1.0;
  return 1.0 + (completed - 1) * 0.2; // +20% per additional completer, max +60% at 4
}

export const useGroupStore = create<GroupState>()((set, get) => ({
  currentSession: null,
  loading: false,
  error: null,

  createSession: async (vaultId, leaderId, runnerName) => {
    set({ loading: true, error: null });
    try {
      const code = randomCode();
      const { data: sessionData, error: sessionErr } = await supabase
        .from('group_sessions')
        .insert({ code, leader_id: leaderId, vault_id: vaultId, status: 'waiting' })
        .select('id, code, leader_id, vault_id, status, expires_at')
        .single();
      if (sessionErr || !sessionData) throw sessionErr ?? new Error('no data');

      await supabase.from('group_members').insert({
        session_id: sessionData.id,
        player_id: leaderId,
        runner_name: runnerName,
      });

      const session: GroupSession = {
        id: sessionData.id,
        code: sessionData.code,
        leaderId: sessionData.leader_id,
        vaultId: sessionData.vault_id,
        status: sessionData.status,
        members: [{ playerId: leaderId, runnerName, completedAt: null }],
        expiresAt: new Date(sessionData.expires_at).getTime(),
      };
      set({ currentSession: session, loading: false });
      return { ok: true, code: sessionData.code };
    } catch (e) {
      set({ loading: false, error: String(e) });
      return { ok: false };
    }
  },

  joinSession: async (code, playerId, runnerName) => {
    set({ loading: true, error: null });
    try {
      const { data: sessions, error: findErr } = await supabase
        .from('group_sessions')
        .select('id, code, leader_id, vault_id, status, expires_at')
        .eq('code', code.toUpperCase())
        .eq('status', 'waiting')
        .limit(1);
      if (findErr || !sessions?.length) throw new Error('Session nicht gefunden');
      const sessionData = sessions[0];
      if (new Date(sessionData.expires_at).getTime() < Date.now()) throw new Error('Session abgelaufen');

      await supabase.from('group_members').upsert({
        session_id: sessionData.id,
        player_id: playerId,
        runner_name: runnerName,
      });

      await get().fetchSession(sessionData.id);
      set({ loading: false });
      return { ok: true, vaultId: sessionData.vault_id };
    } catch (e) {
      set({ loading: false, error: String(e) });
      return { ok: false };
    }
  },

  leaveSession: async (playerId) => {
    const s = get().currentSession;
    if (!s) return;
    // S-7: surface server errors instead of silently clearing client state.
    // Even on error we still drop the local session so the user isn't stuck —
    // the orphaned server row will get GC'd by the next session-cleanup pass.
    const { error } = await supabase.from('group_members')
      .delete()
      .eq('session_id', s.id)
      .eq('player_id', playerId);
    if (error) {
      console.error('[groupStore] leave session DB delete failed', error);
    }
    set({ currentSession: null });
  },

  markCompleted: async (playerId) => {
    const s = get().currentSession;
    if (!s) return;
    await supabase.from('group_members')
      .update({ completed_at: new Date().toISOString() })
      .eq('session_id', s.id)
      .eq('player_id', playerId);
    // Refresh local session
    await get().fetchSession(s.id);
  },

  fetchSession: async (sessionId) => {
    const [sessionRes, membersRes] = await Promise.all([
      supabase
        .from('group_sessions')
        .select('id, code, leader_id, vault_id, status, expires_at')
        .eq('id', sessionId)
        .single(),
      supabase
        .from('group_members')
        .select('player_id, runner_name, completed_at')
        .eq('session_id', sessionId),
    ]);
    if (sessionRes.error || !sessionRes.data) return;
    const d = sessionRes.data;
    const members: GroupMember[] = (membersRes.data ?? []).map((m) => ({
      playerId: m.player_id,
      runnerName: m.runner_name,
      completedAt: m.completed_at ? new Date(m.completed_at).getTime() : null,
    }));
    set({
      currentSession: {
        id: d.id, code: d.code, leaderId: d.leader_id,
        vaultId: d.vault_id, status: d.status,
        members, expiresAt: new Date(d.expires_at).getTime(),
      },
    });
  },

  clearSession: () => set({ currentSession: null, error: null }),

  subscribeRealtime: (sessionId) => {
    const channel = supabase
      .channel(`group_${sessionId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'group_members',
        filter: `session_id=eq.${sessionId}` },
        () => { void get().fetchSession(sessionId); })
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  },
}));
